import '../App.css';
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

function formatDate(iso) {
    return new Date(iso).toLocaleString();
}

function formatCost(usd) {
    return `$${usd.toFixed(6)}`;
}

function weekStart() {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    d.setHours(0, 0, 0, 0);
    return d;
}

export default function Costs() {
    const [agentJobs, setAgentJobs] = useState([]);
    const [marketingJobs, setMarketingJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await authgear.refreshAccessTokenIfNeeded();
            const headers = { Authorization: `Bearer ${authgear.accessToken}` };
            const [agentRes, marketingRes] = await Promise.all([
                axios.get(`${base_url}/agent/costs`, { headers }),
                axios.get(`${base_url}/marketing/costs`, { headers }),
            ]);
            setAgentJobs(agentRes.data);
            setMarketingJobs(marketingRes.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const allEntries = [
        ...agentJobs.map(j => ({
            id: j._id,
            date: new Date(j.createdAt),
            source: "Code Agent",
            description: j.prompt ? j.prompt.slice(0, 80) + (j.prompt.length > 80 ? "…" : "") : j.repo || "",
            costUsd: j.costUsd,
        })),
        ...marketingJobs.map(j => {
            let desc = "";
            try {
                const a = JSON.parse(j.proposedAction);
                desc = a.description ? a.description.slice(0, 80) + (a.description.length > 80 ? "…" : "") : a.actionType || "";
            } catch (_) {}
            return {
                id: j._id,
                date: new Date(j.createdAt),
                source: "Management Agent",
                description: desc,
                costUsd: j.costUsd,
                falCostUsd: j.falCostUsd,
                falNumClips: j.falNumClips,
            };
        }),
    ].sort((a, b) => b.date - a.date);

    const ws = weekStart();
    const weekEntries = allEntries.filter(e => e.date >= ws);
    const weekTotal = weekEntries.reduce((s, e) => s + e.costUsd, 0);
    const allTimeTotal = allEntries.reduce((s, e) => s + e.costUsd, 0);

    return (
        <div className="page-container">
            <Header />
            <div style={{ padding: "2rem", maxWidth: 900, margin: "0 auto" }}>
                <h1 style={{ marginBottom: "1.5rem" }}>API Cost Log</h1>

                {loading && <p>Loading...</p>}
                {error && <p style={{ color: "#f87171" }}>Error: {error}</p>}

                {!loading && !error && (
                    <>
                        <div style={{ display: "flex", gap: "2rem", marginBottom: "2rem" }}>
                            <div style={{ background: "#1e1e2e", padding: "1rem 1.5rem", borderRadius: 8 }}>
                                <div style={{ fontSize: "0.8rem", color: "#888", marginBottom: 4 }}>This week</div>
                                <div style={{ fontSize: "1.6rem", fontWeight: "bold", color: "#4ecdc4" }}>
                                    {formatCost(weekTotal)}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "#888" }}>{weekEntries.length} calls</div>
                            </div>
                            <div style={{ background: "#1e1e2e", padding: "1rem 1.5rem", borderRadius: 8 }}>
                                <div style={{ fontSize: "0.8rem", color: "#888", marginBottom: 4 }}>All time</div>
                                <div style={{ fontSize: "1.6rem", fontWeight: "bold", color: "#a78bfa" }}>
                                    {formatCost(allTimeTotal)}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "#888" }}>{allEntries.length} calls</div>
                            </div>
                        </div>

                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                            <thead>
                                <tr style={{ borderBottom: "1px solid #333", textAlign: "left" }}>
                                    <th style={{ padding: "8px 12px", color: "#888" }}>Date</th>
                                    <th style={{ padding: "8px 12px", color: "#888" }}>Source</th>
                                    <th style={{ padding: "8px 12px", color: "#888" }}>Description</th>
                                    <th style={{ padding: "8px 12px", color: "#888", textAlign: "right" }}>Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allEntries.length === 0 && (
                                    <tr>
                                        <td colSpan={4} style={{ padding: "2rem", textAlign: "center", color: "#888" }}>
                                            No cost records yet
                                        </td>
                                    </tr>
                                )}
                                {allEntries.map(entry => (
                                    <tr key={entry.id} style={{ borderBottom: "1px solid #222" }}>
                                        <td style={{ padding: "10px 12px", color: "#aaa", whiteSpace: "nowrap" }}>
                                            {formatDate(entry.date)}
                                        </td>
                                        <td style={{ padding: "10px 12px" }}>
                                            <span style={{
                                                background: entry.source === "Code Agent" ? "#1e3a5f" : "#2d1b4e",
                                                color: entry.source === "Code Agent" ? "#60a5fa" : "#c084fc",
                                                padding: "2px 8px",
                                                borderRadius: 4,
                                                fontSize: "0.8rem",
                                            }}>
                                                {entry.source}
                                            </span>
                                        </td>
                                        <td style={{ padding: "10px 12px", color: "#ccc" }}>
                                            {entry.description}
                                        </td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", fontFamily: "monospace", color: "#4ecdc4" }}>
                                            {formatCost(entry.costUsd)}
                                            {entry.falCostUsd != null && (
                                                <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginTop: "2px" }}>
                                                    fal ${entry.falCostUsd.toFixed(2)}
                                                    {entry.falNumClips > 0 && <span style={{ color: "#555", fontSize: "0.7rem" }}> ({entry.falNumClips}×)</span>}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </div>
    );
}
